import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Sparkles, Flame } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_META = {
  daily_flow:    { emoji: '📝', label: 'Idag-lista' },
  room_rotation: { emoji: '🗓️', label: 'Städ-rutan' },
  habits:        { emoji: '🎯', label: 'Vanor' },
  kickstart:     { emoji: '📸', label: 'Kickstart' },
};

const WEEKDAY_SHORT = ['M', 'T', 'O', 'T', 'F', 'L', 'S'];

export default function MicroWinsCounter({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [t, w] = await Promise.all([
        axios.get(`${API}/micro-wins/today`, { withCredentials: true }),
        axios.get(`${API}/micro-wins/week`, { withCredentials: true }),
      ]);
      setData(t.data);
      setWeek(w.data);
    } catch (e) {
      // silent — this is a widget, don't spam toasts
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  // gentle auto-refresh every 60s so wins add up in near-realtime
  useEffect(() => {
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  if (loading || !data) return null;

  const max = Math.max(1, ...(week?.days || []).map(d => d.total));

  return (
    <div
      className="neo-card p-4 mb-4 bg-gradient-to-br from-[#FFF5D1] via-[#FFE5B4] to-[#FFC8DD]"
      data-testid="micro-wins-counter"
    >
      <div className="flex items-center gap-4">
        {/* Big number */}
        <div className="flex-shrink-0 flex flex-col items-center justify-center min-w-[80px]">
          <div className="text-5xl md:text-6xl font-['Fredoka'] font-bold leading-none text-black">
            {data.total}
          </div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-gray-700 mt-1">
            vinster idag
          </div>
        </div>

        {/* Message + streak + breakdown */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 flex-shrink-0" />
            <div className="text-sm md:text-base font-['Fredoka'] font-bold leading-snug">
              {data.message}
            </div>
          </div>

          {/* Source pills */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {Object.entries(data.by_source).map(([k, v]) => {
              const meta = SOURCE_META[k] || { emoji: '✨', label: k };
              return (
                <div
                  key={k}
                  className={`px-2 py-0.5 rounded-full border-2 border-black text-xs font-bold flex items-center gap-1 ${
                    v > 0 ? 'bg-white' : 'bg-white/40 text-gray-500'
                  }`}
                  title={meta.label}
                  data-testid={`microwin-source-${k}`}
                >
                  <span>{meta.emoji}</span>
                  <span>{v}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Streak */}
        {data.streak_days > 0 && (
          <div
            className="hidden sm:flex flex-col items-center px-3 py-2 bg-white rounded-2xl border-2 border-black flex-shrink-0"
            data-testid="microwin-streak"
          >
            <Flame className="w-5 h-5 text-orange-500" />
            <div className="text-lg font-['Fredoka'] font-bold leading-none mt-0.5">
              {data.streak_days}d
            </div>
            <div className="text-[9px] text-gray-600 uppercase tracking-wider">streak</div>
          </div>
        )}
      </div>

      {/* Week mini-bar */}
      {week?.days && (
        <div className="mt-3 flex items-end gap-1 h-10">
          {week.days.map((d, idx) => {
            const isToday = idx === week.days.length - 1;
            const h = Math.round((d.total / max) * 100);
            return (
              <div key={d.day} className="flex-1 flex flex-col items-center gap-0.5">
                <div className="flex-1 w-full bg-white/50 rounded-t-md border border-black/20 flex items-end overflow-hidden">
                  <div
                    className={`w-full transition-all ${isToday ? 'bg-black' : 'bg-gray-700'}`}
                    style={{ height: `${d.total > 0 ? h : 4}%` }}
                    title={`${d.total} vinster`}
                  />
                </div>
                <div className={`text-[9px] font-bold ${isToday ? 'text-black' : 'text-gray-600'}`}>
                  {WEEKDAY_SHORT[d.weekday]}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
